import {ReactNode} from 'react';

/**
 * Le panneau de contenu, et le seul endroit qui sache comment il est fait.
 *
 * ## Ce qu'il retire
 *
 * Cinq composants écrivaient à la main `content-panel`, parfois `content-panel--secondary`, puis
 * un `<h2 className="section-heading">` et un `<p className="section-copy">`. Quatre chaînes de
 * classes recopiées, donc quatre occasions d'en oublier une — et un changement de structure à
 * faire cinq fois.
 *
 * Pour un prochain client, c'est aussi le fichier unique où l'on change la forme d'un panneau.
 *
 * ## Des paragraphes, plutôt que deux sauts de ligne
 *
 * Le texte s'écrivait `{premier}<br/><br/>{second}` dans un seul `<p>`. Visuellement identique,
 * mais un lecteur d'écran y entend une seule phrase interminable, et le style ne peut plus
 * espacer des paragraphes qui n'existent pas. `paragraphs` en fait de vrais `<p>`.
 */
export const SectionCopy = ({
    paragraphs,
    className = '',
}: {
    readonly paragraphs: readonly string[];
    readonly className?: string;
}) => {
    const utiles = paragraphs.filter((texte) => texte.trim() !== '');
    if (utiles.length === 0) return null;

    return (
        <div className={`section-copy ${className}`.trim()}>
            {utiles.map((texte) => (
                <p key={texte}>{texte}</p>
            ))}
        </div>
    );
};

const SectionPanel = ({
    id,
    title,
    variant = 'primary',
    className = '',
    children,
}: {
    readonly id?: string;
    readonly title?: string;
    /** Variante conservée pour les composants existants ; la palette est définie dans design.css. */
    readonly variant?: 'primary' | 'secondary';
    readonly className?: string;
    readonly children?: ReactNode;
}) => (
    <article
        id={id}
        className={[
            'content-panel',
            variant === 'secondary' ? 'content-panel--secondary' : '',
            className,
        ]
            .filter(Boolean)
            .join(' ')}
    >
        {title !== undefined && <h2 className="section-heading">{title}</h2>}
        {children}
    </article>
);

export default SectionPanel;
